import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Config from '../../../../../Config';
import type ProblemDomainSelectionBlockData from '../../../../../Models/Content/ProblemDomainSelectionBlockData';
import { replacePlaceholders } from '../../../../../utilities/StringHelpers/TitleHelpers';
import { iconBackChevron, iconDomainRowChevron } from '../../images';
import { getChosenProductNameForReport, getProblemDomainGreetingName, resolveDomainRowFromCms } from '../../problemDomainHelpers';
import type { SubscriptionOption } from '../SubscriptionSelectionPage/SubscriptionSelectionPage';
import { reportDWH } from '../../problemDomainBlockSlice';
import '../DomainSelectionPage/domainSelectionPage.scss';
import './unidentifiedFaultPage.scss';

type CmsImageRef = {
  expandedValue?: { url?: string };
  value?: { url?: string };
};

type CmsPageRef = {
  expandedValue?: { url?: string };
  value?: string | { url?: string };
};

type CommonFaultItem = {
  icon?: CmsImageRef;
  faultName?: { value?: string };
  guidePage?: CmsPageRef;
  contentLink?: { id?: number | string };
};

type ProblemDomainItem = {
  productWorld?: { value?: unknown } | unknown;
  contentLink?: { id?: number | string };
  unidentifiedFaultTitle?: { value?: string };
  unidentifiedFaultHelpPrompt?: { value?: string };
  commonFaultItems?: { expandedValue?: CommonFaultItem[] };
};

type Props = {
  iContent: ProblemDomainSelectionBlockData;
  selectedSubscription: SubscriptionOption;
  onBack: () => void;
};

const mediaUrl = (imageProp: CmsImageRef | undefined): string | null => {
  if (!imageProp) {
    return null;
  }
  const path = imageProp.expandedValue?.url || imageProp.value?.url;
  return path ? Config.epiBaseUrl + path : null;
};

const pageUrl = (pageRef: CmsPageRef | undefined): string | null => {
  if (!pageRef) {
    return null;
  }
  const raw =
    pageRef.expandedValue?.url ||
    (typeof pageRef.value === 'object' ? pageRef.value?.url : pageRef.value);
  if (!raw || typeof raw !== 'string') {
    return null;
  }
  if (raw.startsWith('http') || raw.startsWith('/')) {
    return raw;
  }
  return Config.epiBaseUrl + raw;
};

const UnidentifiedFaultPage = ({ iContent, selectedSubscription, onBack }: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const { selectedWorld: productWorld, selectedDomainContentLinkId: domainContentLinkId } = useSelector(
    (state: {
      problemDomainBlock?: {
        selectedWorld?: number | null;
        selectedDomainContentLinkId?: number | string | null;
      };
    }) => state.problemDomainBlock ?? {},
  );

  const loginData = useSelector(
    (state: {
      authentication?: {
        loginData?: {
          userDetails?: {
            firstName?: string;
            contactNumber?: string;
          };
        };
      };
    }) => state.authentication?.loginData,
  );

  useEffect(() => {
    dispatch(reportDWH({
      actionType: 'OptionalMalfunctionsListPage',
      status: 'Ongoing',
      messageText: 'Optional Malufunctions Page Displayed',
      param1: getChosenProductNameForReport(productWorld),
    }));
  }, [dispatch, productWorld]);

  const domainRow = useMemo(() => {
    const domainItems = (iContent?.domainItems?.expandedValue || []) as unknown as ProblemDomainItem[];
    if (productWorld == null) {
      return undefined;
    }
    return resolveDomainRowFromCms(domainItems, productWorld, domainContentLinkId);
  }, [iContent?.domainItems?.expandedValue, productWorld, domainContentLinkId]);

  const titleHtml = useMemo(() => {
    const template = domainRow?.unidentifiedFaultTitle?.value ?? '';
    if (!template) {
      return '';
    }

    const greetingName = getProblemDomainGreetingName(
      loginData?.userDetails,
      selectedSubscription?.raw,
    );
    const firstName =
      String(loginData?.userDetails?.firstName ?? '').trim() ||
      String(selectedSubscription?.raw?.productFirstName ?? '').trim();

    const title = replacePlaceholders(template, { firstName });
    if (title.trim().startsWith('היי')) {
      return greetingName ? title.replace(/^היי(?:\s[^,<]*)?,/, `היי ${greetingName},`) : title;
    }

    const greeting = greetingName ? `היי ${greetingName},` : 'היי,';
    return `${greeting}<br />${title}`;
  }, [domainRow, loginData, selectedSubscription]);

  const helpPrompt = domainRow?.unidentifiedFaultHelpPrompt?.value ?? '';
  const faultItems = (domainRow?.commonFaultItems?.expandedValue || []) as CommonFaultItem[];

  const bgDesktop = mediaUrl(iContent?.backgroundImageDesktop as unknown as CmsImageRef);
  const bgMobile = mediaUrl(iContent?.backgroundImageMobile as unknown as CmsImageRef);

  const sectionStyle = {
    ...(bgDesktop ? { '--prob-domain-bg-desktop': `url("${bgDesktop}")` } : {}),
    ...(bgMobile ? { '--prob-domain-bg-mobile': `url("${bgMobile}")` } : {}),
  } as React.CSSProperties;

  const handleFaultItemClick = (item: CommonFaultItem) => {
    dispatch(reportDWH({
      actionType: 'OptionalMalufunctionsListPage',
      status: 'Ongoing',
      messageText: 'Malufunction Component Clicked',
      param1: getChosenProductNameForReport(productWorld),

    }));

    const href = pageUrl(item.guidePage);
    if (href) {
      window.location.href = href;
    }
  };

  return (
    <section className="problem-domain-selection problem-domain-unidentified" style={sectionStyle}>
      <header className="problem-domain-selection__top">
        <button type="button" className="problem-domain-selection__back" onClick={onBack}>
          <span className="problem-domain-selection__back-content">
            <img
              src={iconBackChevron}
              alt=""
              width={16}
              height={16}
              className="problem-domain-selection__back-icon"
              aria-hidden
            />
            <span>חזרה</span>
          </span>
        </button>
      </header>

      <div className="problem-domain-selection__shell problem-domain-unidentified__shell">
        {titleHtml || helpPrompt ? (
          <div className="problem-domain-unidentified__intro">
            {titleHtml ? (
              <h1
                className="problem-domain-unidentified__title"
                dangerouslySetInnerHTML={{ __html: titleHtml }}
              />
            ) : null}

            {helpPrompt ? (
              <p className="problem-domain-unidentified__subtitle">{helpPrompt}</p>
            ) : null}
          </div>
        ) : null}

        {faultItems.length > 0 ? (
          <ul className="problem-domain-selection__list problem-domain-unidentified__list" role="list">
            {faultItems.map((item, idx) => {
              const label = item.faultName?.value?.trim() || `תקלה ${idx + 1}`;
              const iconUrl = mediaUrl(item.icon);
              const key = item.contentLink?.id ?? idx;

              return (
                <li key={key} className="problem-domain-selection__item">
                  <button
                    type="button"
                    className="problem-domain-selection__card"
                    aria-label={label}
                    onClick={() => handleFaultItemClick(item)}
                  >
                    <span className="problem-domain-selection__card-main">
                      {iconUrl ? (
                        <span className="problem-domain-selection__icon-wrap">
                          <img src={iconUrl} alt="" className="problem-domain-selection__icon" />
                        </span>
                      ) : null}
                      <span className="problem-domain-selection__label">{label}</span>
                    </span>
                    <span className="problem-domain-selection__chevron-btn" aria-hidden>
                      <img
                        src={iconDomainRowChevron}
                        width={9}
                        height={14}
                        className="problem-domain-selection__chevron-img"
                        alt=""
                      />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </section>
  );
};

export default UnidentifiedFaultPage;
